import { useState, useCallback, useMemo, useEffect } from "react";
import { achievements, Achievement } from "@/data/achievements";
// Ids only, so the home page does not load every chronicle's ledger text.
import { chronicleMemoryFlagIds } from "@/data/chronicle-memory-flag-ids";
import { getUnlockedFlags } from "@/engine/chronicleMemoryStore";

const STORAGE_KEY = "chronicles-completed";
/** Stores achievement ids the player has already seen unlock — used by the
 *  Trophy nav button to show a "new" badge until the player opens the
 *  showcase. */
const SEEN_KEY = "achievements-seen";

function getCompleted(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function getSeen(): string[] {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) || "[]");
  } catch {
    return [];
  }
}

/**
 * Returns true iff every defined memory flag for `chronicleId` has
 * been unlocked. A chronicle with no defined flags is treated as
 * "complete" for this purpose so the completionist achievements don't
 * stall on chronicles that haven't shipped flag tables yet.
 */
function chronicleHasAllFlags(chronicleId: string): boolean {
  const defined = chronicleMemoryFlagIds[chronicleId] || [];
  if (defined.length === 0) return true;
  const unlocked = new Set(getUnlockedFlags(chronicleId));
  return defined.every((id) => unlocked.has(id));
}

function isUnlocked(a: Achievement, completed: Set<string>): boolean {
  if (a.chronicleIds.length === 0) return false; // placeholder
  if (!a.chronicleIds.every((id) => completed.has(id))) return false;
  // Completionist tier: also require every memory flag in scope.
  if (a.requireAllFlags) {
    if (!a.chronicleIds.every((id) => chronicleHasAllFlags(id))) return false;
  }
  return true;
}

export function useAchievements() {
  const [completedChronicles, setCompletedChronicles] = useState<string[]>(getCompleted);
  const [seenAchievements, setSeenAchievements] = useState<string[]>(getSeen);
  /** Achievements that crossed locked → unlocked on the most recent
   *  markCompleted() call. Consumed by the celebration banner shown on
   *  the chronicle's outcome screen and cleared after display. */
  const [newlyUnlocked, setNewlyUnlocked] = useState<Achievement[]>([]);

  // Memory-flag changes happen outside this hook (chronicleMemoryStore
  // writes via Play.tsx). Bump a counter when any localStorage write
  // could affect completionist unlock state, so the achievementProgress
  // memo recomputes. Cheap because the storage event is rare.
  const [flagsRev, setFlagsRev] = useState(0);
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === "chronicles_memory_flags") setFlagsRev((r) => r + 1);
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const markCompleted = useCallback((chronicleId: string) => {
    setCompletedChronicles((prev) => {
      if (prev.includes(chronicleId)) return prev;
      const next = [...prev, chronicleId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));

      // Detect achievements that crossed the unlock threshold on this
      // completion. Diff the unlocked set before and after.
      const before = new Set(prev);
      const after = new Set(next);
      const justUnlocked = achievements.filter(
        (a) => !isUnlocked(a, before) && isUnlocked(a, after),
      );
      if (justUnlocked.length > 0) setNewlyUnlocked(justUnlocked);

      return next;
    });
  }, []);

  /** Clear the post-completion celebration list (called by the banner
   *  after the player dismisses or auto-dismiss timer fires). */
  const acknowledgeNewlyUnlocked = useCallback(() => {
    setNewlyUnlocked([]);
  }, []);

  /** Mark all currently-unlocked achievements as seen. Called when the
   *  player opens the Achievement Showcase modal. */
  const markAllSeen = useCallback(() => {
    setSeenAchievements(() => {
      const completed = new Set(completedChronicles);
      const next = achievements.filter((a) => isUnlocked(a, completed)).map((a) => a.id);
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
      return next;
    });
  }, [completedChronicles]);

  const achievementProgress = useMemo(() => {
    const completed = new Set(completedChronicles);
    return achievements.map((a) => {
      // For completionist achievements, only count chronicles that are
      // BOTH completed AND have every memory flag unlocked. Otherwise
      // a chronicle the player has finished once but not exhausted
      // (e.g. still has untouched endings/discoveries) would count
      // toward "True Chronicler of Europe" — which is supposed to
      // require full discovery across every chronicle.
      const counted = a.requireAllFlags
        ? a.chronicleIds.filter((id) => completed.has(id) && chronicleHasAllFlags(id)).length
        : a.chronicleIds.filter((id) => completed.has(id)).length;
      return {
        ...a,
        completed: counted,
        total: a.chronicleIds.length,
        unlocked: isUnlocked(a, completed),
      };
    });
    // flagsRev intentionally included — bumping it forces a recompute
    // when chronicle memory flags change (drives the completionist
    // unlock state without a manual re-render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedChronicles, flagsRev]);

  /** Count of unlocked achievements the player has not yet seen — drives
   *  the Trophy nav button's badge on Index.tsx. */
  const unseenUnlockCount = useMemo(() => {
    const seen = new Set(seenAchievements);
    return achievementProgress.filter((a) => a.unlocked && !seen.has(a.id)).length;
  }, [achievementProgress, seenAchievements]);

  return {
    completedChronicles,
    markCompleted,
    achievementProgress,
    newlyUnlocked,
    acknowledgeNewlyUnlocked,
    markAllSeen,
    unseenUnlockCount,
  };
}
